import { jsx as _jsx } from "react/jsx-runtime";
import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { List, useDynamicRowHeight, useListRef } from 'react-window';
import { Box } from '../../box';
import { useMounted } from '../../hooks';
import { useListboxHeight } from './use-listbox-height';
const MUI_DEFAULT_ITEM_SIZE = 36;
const MUI_SMALL_ITEM_SIZE = 48;
const ListboxComponent = React.forwardRef((props, ref) => {
    const { children: _children, internalListRef, optionHeight, style, ...restProps } = props;
    const children = _children;
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
        noSsr: true
    });
    const itemCount = children.length;
    const itemSize = smUp ? MUI_DEFAULT_ITEM_SIZE : MUI_SMALL_ITEM_SIZE;
    const rowHeight = useDynamicRowHeight({
        defaultRowHeight: optionHeight ?? itemSize
    });
    const { className, ...divProps } = restProps;
    return (_jsx(Box, { ref: ref, ...divProps, children: _jsx(List, { className: className, listRef: internalListRef, rowCount: itemCount, rowHeight: rowHeight, rowComponent: RowComponent, rowProps: { children }, overscanCount: 4, tagName: "ul", 
            // eslint-disable-next-line react/forbid-component-props
            style: {
                width: '100%',
                ...style
            } }) }));
});
function RowComponent({ index, children, style }) {
    const component = children[index];
    return (_jsx("li", { role: "presentation", style: style, children: component }));
}
RowComponent.displayName = 'RowComponent';
export function useListBox(state, optionHeight, options = []) {
    const { paperRef, maxHeight } = useListboxHeight(state);
    const internalListRef = useListRef(null);
    const mounted = useMounted();
    const slotProps = {
        component: ListboxComponent,
        style: {
            maxHeight
        },
        optionHeight,
        internalListRef
    };
    const scrollToRow = React.useCallback((option) => {
        const { current: listApi } = internalListRef;
        if (option && listApi) {
            const index = options.indexOf(option);
            if (index !== -1) {
                listApi.scrollToRow({ index, align: 'center' });
            }
        }
    }, [internalListRef, options]);
    const onOpen = React.useCallback((option) => {
        setTimeout(() => {
            if (mounted.current) {
                if (Array.isArray(option)) {
                    scrollToRow(option[0] ?? null);
                }
                else {
                    scrollToRow(option);
                }
            }
        }, 0);
    }, [mounted, scrollToRow]);
    const onHighlightChange = React.useCallback((e, option) => {
        if (e.type !== 'mousemove') {
            scrollToRow(option);
        }
    }, [scrollToRow]);
    return {
        paperRef,
        slotProps,
        onOpen,
        onHighlightChange
    };
}
